import { type QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import useDebounceFunction from '../../components/Hooks/useDebouncedFunction';
import { useApplicationContext } from '../../modules/context/ApplicationContext/ApplicationContext';
import { useMutateInfiniteDataQuery } from './hooks/useMutateInfiniteDataQuery/useMutateInfiniteDataQuery';
import { InfiniteDataQueryPage, UseInfiniteDataQueryParams } from './useInfiniteDataQuery.types';
import { UseInfiniteDataQueryUtils } from './utils/useInfiniteDataQuery.utils';

const DEFAULT_MAX_PAGES = 5;

const resolveMaxPages = (raw: number | null | undefined): number | undefined => {
  if (raw === undefined) {
    return DEFAULT_MAX_PAGES;
  }
  if (raw === null) {
    return undefined;
  }
  return raw;
};

const useInfiniteDataQuery = <PageData>({
  limit,
  queryFn,
  queryKey,
  isEnabled,
  defaultOrderBy,
  refetchOnMount,
  staleTime,
  maxPages: rawMaxPages,
}: UseInfiniteDataQueryParams<PageData>) => {
  const maxPages = resolveMaxPages(rawMaxPages);
  const { team } = useApplicationContext();
  const { handleDebounceFunction: handleDebounceFetchNextPage } = useDebounceFunction({
    debounceTime: 1,
  });

  const queryKeyWithTeamId: QueryKey = [team?.id, limit, defaultOrderBy, ...queryKey];

  const {
    data,
    error,
    refetch,
    isError,
    isLoading,
    isSuccess,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<
    InfiniteDataQueryPage<PageData>,
    Error,
    { pages: InfiniteDataQueryPage<PageData>[]; pageParams: number[] },
    QueryKey,
    number
  >({
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    refetchOnMount,
    staleTime,
    maxPages,
    queryKey: queryKeyWithTeamId,
    initialPageParam: 1,
    queryFn: async ({ pageParam, signal }) => {
      const skip = (pageParam - 1) * limit;

      const queryFnResult = await queryFn({ skip, limit, signal, order: defaultOrderBy });

      return UseInfiniteDataQueryUtils.toCachedPage({
        page: queryFnResult,
        currentPage: pageParam,
        limit,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      const { currentPage } = lastPage;
      const countData = allPages[0]?.countData;

      if (!countData) {
        return lastPage.isFullPage ? currentPage + 1 : undefined;
      }

      const hasMorePages = currentPage * limit < countData.total;

      return hasMorePages ? currentPage + 1 : undefined;
    },
  });

  const mutateMethods = useMutateInfiniteDataQuery<PageData>({
    queryKey: queryKeyWithTeamId,
  });

  const handleFetchNextPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      return fetchNextPage();
    }
  };

  return {
    mutateMethods,
    query: {
      error,
      isError,
      refetch,
      isLoading,
      isSuccess,
      isFetching,
      hasNextPage,
      isFetchingNextPage,
      queryKey: queryKeyWithTeamId,
      pageParams: data?.pageParams,
      countData: data?.pages?.[0]?.countData,
      pagesData: data?.pages?.flatMap((page) => page.data),
      fetchNextPage: () => handleDebounceFetchNextPage(handleFetchNextPage),
    },
  };
};

export default useInfiniteDataQuery;
